'use client';

import { usePathname } from 'next/navigation';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSession } from 'next-auth/react';
import { Flex } from '@eqi-investimentos/react';
import { captureException } from '@sentry/nextjs';
import {
  routesDoNotSendVisit,
  routesShowDisclaimerWebMobile,
} from '@/config/routesConfig';
import { useSendDevice } from '@/modules/Customer/http/client/mutations/useSendDevice';
import { useSendVisitPath } from '@/modules/Customer/http/client/mutations/useSendVisitPath';
import useGeoLocation from '@/hooks/useGeoLocation';
import { Alert } from '@/components/elements/Alert';
import { WebMobileDisclaimer } from '@/components/sections/WebMobileDisclaimer';
import { getCookie, setCookie } from '@/utils/cookies';
import { IfRender } from '@/utils/jsx';

export const ConfigContext = createContext({} as ConfigContextData);

export const ConfigProvider = ({ children }: Readonly<PropsWithChildren>) => {
  const pathname = usePathname();
  const { status } = useSession();

  const { mutate: sendVisitedPath } = useSendVisitPath();
  const { mutate: sendDevice } = useSendDevice();

  const { geoData, hasGeoData } = useGeoLocation();

  const [isMobile, setIsMobile] = useState(false);
  const [alertToast, setAlertToast] = useState<string | null>(null);

  const verifyScreenSize = (size: number) => setIsMobile(size < 876);

  const handleAlert = (label: string) => {
    setAlertToast(label);
    setTimeout(() => setAlertToast(null), 3000);
  };

  const saveXcodeInCookies = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const xcode = urlParams.get('xcode');

      if (xcode) {
        setCookie('xcode', xcode);
      }
    } catch (error) {
      console.error('Error saving xcode in cookies:', error);
    }
  };

  useEffect(() => {
    try {
      const deviceCode = getCookie('deviceCode');
      if (routesDoNotSendVisit.includes(pathname) || !deviceCode) return;
      const [label] = pathname.split('?');

      sendVisitedPath({
        label,
        origin: 'web',
        time: false,
        device_code: deviceCode,
      });
    } catch (error) {
      captureException(
        new Error(`Erro ao enviar /visit page route ${pathname}` + error)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, status]);

  useEffect(() => {
    saveXcodeInCookies();

    window.addEventListener('resize', () =>
      verifyScreenSize(window.innerWidth)
    );
    verifyScreenSize(window.innerWidth);

    return () => {
      window.removeEventListener('resize', () =>
        verifyScreenSize(window.innerWidth)
      );
    };
  }, []);

  useEffect(() => {
    if (!hasGeoData) return;

    let deviceCode = getCookie('deviceCode');
    const lastSentTimestamp = getCookie('lastSentDeviceTimestamp');
    const currentTime = Date.now();
    const tenMinutesInMs = 10 * 60 * 1000;

    if (!deviceCode) {
      deviceCode = crypto.randomUUID();
      setCookie('deviceCode', deviceCode);
    }

    if (
      !lastSentTimestamp ||
      currentTime - parseInt(lastSentTimestamp, 10) > tenMinutesInMs
    ) {
      const agent = navigator.userAgent;

      sendDevice({
        device_code: deviceCode,
        type: 'web',
        agent,
        device_ip: geoData?.ip,
        city: geoData?.city,
        state: geoData?.region,
        country: geoData?.country,
      });

      setCookie('lastSentDeviceTimestamp', currentTime.toString());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoData, hasGeoData]);

  return (
    <ConfigContext.Provider value={{ isMobile, handleAlert }}>
      <IfRender condition={!!alertToast}>
        <Flex
          pos="absolute"
          top="5"
          justifyContent="center"
          w="full"
          zIndex="20"
        >
          <Alert description={alertToast ?? ''} />
        </Flex>
      </IfRender>
      <IfRender
        condition={
          isMobile && !routesShowDisclaimerWebMobile.includes(pathname)
        }
      >
        <WebMobileDisclaimer />
      </IfRender>
      <IfRender
        condition={
          !isMobile || routesShowDisclaimerWebMobile.includes(pathname)
        }
      >
        {children}
      </IfRender>
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);

type ConfigContextData = {
  isMobile: boolean;
  handleAlert: (label: string) => void;
};
